// =====================================================
// 📁 QuotationProductCardView.tsx
// =====================================================

import { useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import useProductPanel from '../../../../../../hooks/productpanel/useProductPanel';
import { ProductPanelItem } from '../../../../../../types/productpanel/ProductPanelItem';
import QuotationProductPlaceholder from '../QuotationProductPlaceholder';
import QuotationProductItem from '../item/QuotationProductItem';
import UniversalTablePagination from '../../../../../Tables/universalRecord/UniversalTablePagination';

interface Props {
  mode?: 'list' | 'card';

  onAddProductClick: (product: ProductPanelItem) => void;
}

const QuotationProductCardView: React.FC<Props> = ({
  mode,
  onAddProductClick,
}) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const {
    filteredProducts,

    paginator,

    pageSize,

    fetchFilteredProducts,

    currentFilter,

    updatePageSize,

    updateCurrentPage,
  } = useProductPanel();

  // =====================================================
  // 🔹 Pagination
  // =====================================================
  const handlePageChange = (page: number) => {
    updateCurrentPage(page);

    fetchFilteredProducts(currentFilter || {}, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);

    updateCurrentPage(0);

    fetchFilteredProducts(currentFilter || {}, 0, size);
  };

  // =====================================================
  // 🔹 Unique Products
  // =====================================================
  const uniqueProducts = useMemo(() => {
    return filteredProducts.filter(
      (p, index, self) =>
        index === self.findIndex((q) => q.id === p.id && q.name === p.name)
    );
  }, [filteredProducts]);

  // =====================================================
  // 🔹 Empty
  // =====================================================
  if (uniqueProducts.length === 0) {
    return <QuotationProductPlaceholder mode={mode} />;
  }

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div className="space-y-4">
      {/* ===================================================== */}
      {/* 🔹 Grid */}
      {/* ===================================================== */}

      <div
        className="
        grid

        grid-cols-1

        gap-4

        sm:grid-cols-2

        lg:grid-cols-3

        xl:grid-cols-4

        2xl:grid-cols-4
      "
      >
        <AnimatePresence mode="popLayout">
          {uniqueProducts.map((product, index) => (
            <motion.div
              key={`${product.id}-${index}`}
              layout
              initial={{
                opacity: 0,
                scale: 0.95,
                y: 12,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                y: 12,
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut',
              }}
              className="h-full"
            >
              <QuotationProductItem
                product={product}
                mode="card"
                onAddProductClick={onAddProductClick}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Pagination */}
      {/* ===================================================== */}

      <div
        className="
        mt-4

        w-full

        overflow-x-auto
      "
      >
        <UniversalTablePagination
          paginator={paginator}
          data={uniqueProducts}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default QuotationProductCardView;
